import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined'
import { CircularProgress, Link, Typography } from '@mui/material'
import {
  GridActionsCellItem,
  type GridRowParams,
  type GridRowSelectionModel,
} from '@mui/x-data-grid'
import { useGetSingleMedia } from '../hooks/useGetSingleMedia'
import type { OpenModal } from '../hooks/useModal'
import { useSetArchived } from '../hooks/useSetArchived'
import { useSetNonCurrent } from '../hooks/useSetNonCurrent'
import { useSetVerified } from '../hooks/useSetVerified'
import type { MediaToRefresh } from '../types'

export const Actions = (
  params: GridRowParams<MediaToRefresh>,
  openModal: OpenModal,
  selectionModel: GridRowSelectionModel,
) => {
  const title = params.row.title
  const mediaId = params.row.mediaId.toString()
  const mediaNumber = params.row.mediaNumber
  const mediaCode = params.row.mediaCode
  const { setNonCurrent } = useSetNonCurrent()
  const { setVerified } = useSetVerified()
  const { setArchived } = useSetArchived()
  const { openDetails, isFetching } = useGetSingleMedia()
  const mediaLabel = (
    <Typography component="span" fontWeight="bold">
      {mediaNumber}: {title}
    </Typography>
  )
  const disabled = selectionModel.length > 1

  return [
    ...(params.row.status === 'C'
      ? [
          <GridActionsCellItem
            key="non-current"
            label="Set to Non-Current"
            title="Set to Non-Current"
            icon={
              <RemoveCircleOutlineOutlinedIcon
                color={disabled ? 'disabled' : 'info'}
              />
            }
            disabled={disabled}
            onClick={() =>
              openModal(<>Set media {mediaLabel} to Non-Current?</>, () =>
                setNonCurrent([mediaCode]),
              )
            }
          />,
        ]
      : []),
    <GridActionsCellItem
      key="verify"
      label="Verify Media"
      title="Verify Media"
      icon={
        <CheckCircleOutlinedIcon color={disabled ? 'disabled' : 'success'} />
      }
      disabled={disabled}
      onClick={() =>
        openModal(<>Verify media {mediaLabel}?</>, () =>
          setVerified([mediaCode]),
        )
      }
    />,
    <GridActionsCellItem
      key="archive"
      label="Archive Media"
      title="Archive Media"
      icon={<ArchiveOutlinedIcon color={disabled ? 'disabled' : 'error'} />}
      disabled={disabled}
      onClick={() =>
        openModal(
          <>
            Archive media {mediaLabel}? This will store the media in a location
            no longer retrievable in EMC.
            <Typography>
              Contact{' '}
              <Link href="mailto:enginesoftware@catsupport.com">
                enginesoftware@catsupport.com
              </Link>{' '}
              if archived media is needed.
            </Typography>
          </>,
          () => setArchived([mediaCode]),
        )
      }
    />,
    <GridActionsCellItem
      key="details"
      label="Media Details"
      title="Media Details"
      icon={
        isFetching ? (
          <CircularProgress size={20} />
        ) : (
          <DescriptionOutlinedIcon
            fontSize="small"
            color={disabled ? 'disabled' : 'info'}
          />
        )
      }
      disabled={disabled}
      onClick={() => openDetails(mediaId)}
    />,
  ]
}
